<template>
  <div>
    <b-card>
      <template slot="header">
        <h4 class="mt-2 custom-card-header-text">Opportunities</h4>
        <ul class="list-inline float-right header_table">
          <li v-if="communities.length > 1" class="list-inline-item place-center">
            <label for="communityFilter" class="custom-card-header-filter label-bottom-0">Community:</label>
          </li>
          <li v-if="communities.length > 1" class="list-inline-item">
            <b-form-select v-model="selectedCommunity" @change="onFilterChange" class="custom-card-header-filter" id="communityFilter">
              <option :value="null" selected disabled>Community</option>
              <option value="all">All</option>
              <option v-for="community in communities" :key="community._id" v-bind:value="community._id">{{ community.name }}</option>
            </b-form-select>
          </li>
          <li class="list-inline-item place-center">
            <label for="sourceFilter" class="custom-card-header-filter label-bottom-0">Source:</label>
          </li>
          <li class="list-inline-item">
            <b-form-select v-model="selectedSource" @change="onFilterChange" class="custom-card-header-filter" id="sourceFilter">
              <option value="all">All</option>
              <option value="program-page">Program Page</option>
              <option value="dashboard">Dashboard</option>
              <option value="bulk">Bulk</option>
            </b-form-select>
          </li>
          <li>
            <div class="row-bak d-flex-bak align-items-center-bak">
              <datepicker @changed="onDateChange" style="padding-right: 10px;" label="Date Range"></datepicker>
            </div>
          </li>
          <li class="list-inline-item" style="margin-right: 10px;">
            <button
              type="button"
              aria-label="Export"
              :disabled="exporting"
              @click="exportOpportunities()"
              title="Download opportunities data"
              class="btn custom-card-header-filter btn-dropbox tab-focus"
            >
              <i :class="exporting ? 'fa fa-spinner fa-spin' : 'icon-cloud-download'"></i>
            </button>
          </li>
          <li class="list-inline-item">
            <b-button
              variant="dropbox"
              class="create-opp-btn"
              @click="goToCreate"
            >
              <i class="fa fa-plus mr-1" aria-hidden="true"></i>
              Create Opportunity
            </b-button>
          </li>
        </ul>
      </template>
      <b-card-body>
        <div class="bulk-action-slot">
          <transition name="bulk-fade">
            <div v-if="selectedIds.length > 0" class="bulk-action-bar">
              <span class="bulk-action-bar__count">
                <strong>{{ selectedIds.length }}</strong>
                selected
              </span>
              <!-- Pick a target status (highlights), then Apply commits it to
                   the whole selection. Two-step so a bulk change is never one
                   accidental click. -->
              <div class="bulk-action-bar__actions">
                <b-button
                  v-for="a in bulkActions"
                  :key="a.value"
                  size="sm"
                  :variant="bulkPendingStatus === a.value ? a.variant : `outline-${a.variant}`"
                  @click="selectBulkStatus(a.value)"
                >
                  {{ a.label }}
                </b-button>
                <b-button
                  variant="primary"
                  size="sm"
                  :disabled="!bulkPendingStatus || !bulkActions.some((a) => a.value === bulkPendingStatus)"
                  @click="applyBulkPending"
                >
                  Apply
                </b-button>
                <b-button
                  variant="link"
                  size="sm"
                  class="bulk-action-bar__clear"
                  @click="clearSelection"
                >
                  Clear
                </b-button>
              </div>
            </div>
          </transition>
        </div>

        <search-section
          search-placeholder="Search Opportunities..."
          @search="handleSearch"
          @limit-change="handleLimitChange"
        >
          <template slot="before-records">
            <b-form-group
              label="Status:"
              label-for="statusFilter"
              class="mb-0 limit-group"
              style="margin-right: 1rem;"
            >
              <b-form-select
                id="statusFilter"
                v-model="selectedStatus"
                @change="onFilterChange"
                class="custom-limit-select"
              >
                <option value="all">All</option>
                <option value="in_review">In Review</option>
                <option value="active">Approved</option>
                <option value="closed">Closed</option>
                <option value="rejected">Rejected</option>
                <option value="incomplete">Incomplete</option>
                <option value="quarantined">Quarantined</option>
              </b-form-select>
            </b-form-group>
          </template>
        </search-section>

        <div
          v-if="loadingOpportunities"
          class="text-center py-5"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <i class="fa fa-spinner fa-spin fa-2x mb-3 d-block" aria-hidden="true"></i>
          <p class="mb-0 text-muted">Loading opportunities…</p>
        </div>

        <v-server-table
          v-show="!loadingOpportunities"
          ref="opportunitiesTable"
          :url="serverURL"
          :columns="columns"
          :options="options"
          name="dataTable"
          id="dataTable"
          @loaded="onLoaded"
        >
          <template slot="h__selection">
            <b-form-checkbox
              :checked="allCurrentPageSelected"
              :indeterminate="someCurrentPageSelected"
              :disabled="currentPageIds.length === 0"
              class="row-checkbox row-checkbox--header"
              @change="toggleSelectAllCurrentPage"
            />
          </template>
          <template slot="selection" slot-scope="props">
            <b-form-checkbox
              :checked="isSelected(props.row._id)"
              :aria-label="`Select ${props.row.title}`"
              class="row-checkbox"
              @change="toggleSelection(props.row._id)"
            />
          </template>

          <span slot="title" slot-scope="props">
            <a
              :href="opportunityDetailHref(props.row._id)"
              target="_blank"
              rel="noopener"
              class="opp-title-link"
            >
              <strong>{{ props.row.title }}</strong>
            </a>
          </span>
          <span slot="status" slot-scope="props">
            <b-badge :variant="statusVariant(props.row.status)" class="text-capitalize">
              {{ statusLabel(props.row.status) }}
            </b-badge>
          </span>
          <span slot="submittedDate" slot-scope="props">
            {{ props.row.submittedDate | momentDate }}
          </span>
          <span slot="action" slot-scope="props">
            <!-- Single gear dropdown, copied from the Questions (requests.vue)
                 action pattern: same blue btn-dropbox button + icon-settings.
                 View + the four status transitions live here. -->
            <b-dropdown
              right
              boundary="viewport"
              variant="btn btn-dropbox btn-sm tab-focus"
              class="action-status-dropdown"
              :aria-label="`Actions for ${props.row.title}`"
            >
              <template slot="button-content">
                <i class="icon-settings"></i>
              </template>
              <b-dropdown-item
                :href="opportunityDetailHref(props.row._id)"
                target="_blank"
                rel="noopener"
              >
                View Opportunity
              </b-dropdown-item>
              <!-- Status transitions; the row's current status is filtered out. -->
              <b-dropdown-item
                v-for="a in rowStatusActions(props.row.status)"
                :key="a.value"
                @click="applyStatusChange(props.row, a.value)"
              >
                {{ a.label }} Opportunity
              </b-dropdown-item>
            </b-dropdown>
          </span>
        </v-server-table>
      </b-card-body>
    </b-card>

  </div>
</template>

<script>
import axios from 'axios'
import { sanitizeInput } from '../../utils/sanitizer'
var moment = require('moment-timezone')
import datepicker from './components/datePicker'
import SearchSection from '@/views/engagement/components/searchSection.vue'
import {
  MOCK_OPPORTUNITIES,
  setMockOpportunityStatus,
} from './mocks/opportunities'
import {
  CORE_API_URL,
  STATUS_ACTIONS,
  coreApiPageToDashboardList,
  coreApiOpportunityToDashboard,
  opportunityToReportRow,
  REPORT_DATE_FIELDS,
} from './opportunityAdapter'
import { exportCSV } from '@/assets/js/common'

// VUE_APP_USE_OPPORTUNITIES_MOCKS=true falls back to the in-memory mocks.
// Default (unset / 'false') talks to core-api at VUE_APP_CORE_API_URL.
const USE_MOCKS = process.env.VUE_APP_USE_OPPORTUNITIES_MOCKS === 'true'

/**
 * Community scoping — how the page narrows opportunities to the right
 * community for the active user
 * ─────────────────────────────────────────────────────────────────────────
 * Concretely, on `created()` we resolve the initial community in this
 * priority order (matching the pattern in referrals.vue:158-164):
 *
 *   1. `this.$route.params.community_id`   (deep-link via route param)
 *   2. `this.$route.query.community_id`    (deep-link via query string)
 *   3. `localStorage.getItem('user_modal_option')`  (global switcher)
 *   4. fall back to `'all'` if none of the above and the user has multi-
 *      community permissions; or to the user's only community if not.
 */

export default {
  name: 'OpportunityList',
  components: {
    datepicker,
    SearchSection,
  },
  data() {
    return {
      opportunities: [],
      communities: [],
      loadingOpportunities: true,
      selectedCommunity: 'all',
      selectedSource: 'all',
      selectedStatus: 'all',
      exporting: false,
      selectedDates: {
        startDate: new Date(0),
        endDate: new Date(),
      },
      searchQuery: '',
      // SCRUM-598
      statusActions: STATUS_ACTIONS,
      selectedIds: [],
      currentPageIds: [],
      // Target status chosen in the bulk bar, committed on Apply.
      bulkPendingStatus: null,
      perPage: 10,
      // Client-side pagination cache.
      oppsCache: null,
      oppsCacheKey: null,
      token: '',
      columns: ['selection', 'title', 'company', 'status', 'submittedDate', 'openCount', 'clickCount', 'action'],
      options: {
        headings: {
          selection: ' ',
          title: 'Job Title',
          company: 'Employer',
          status: 'Status',
          submittedDate: 'Submitted',
          openCount: 'Opens',
          clickCount: 'Clicks',
          action: 'Action',
        },
        sortable: ['title', 'company', 'status', 'submittedDate', 'openCount', 'clickCount'],
        orderBy: { column: 'submittedDate', ascending: false },
        sortIcon: { base: 'fa', up: 'fa-sort-asc', down: 'fa-sort-desc', is: 'fa-sort' },
        pagination: { chunk: 10, nav: 'fixed' },
        perPage: 10,
        columnsClasses: {
          selection: 'text-center selection-col',
          status: 'text-center col-status',
          submittedDate: 'text-center col-submitted',
          openCount: 'text-center col-opens',
          clickCount: 'text-center col-clicks',
          action: 'text-center col-action',
        },
        requestFunction: async function (data) {
          if (data.query) {
            if (
              data.query.toLowerCase().includes('javascript:') ||
              data.query.toLowerCase().includes('data:') ||
              data.query.toLowerCase().includes('vbscript:')
            ) {
              data.query = ''
            }
            data.query = sanitizeInput(data.query)
          }
          this.$parent.searchQuery = data.query

          // Flip the loading state so the spinner overlay is visible while
          // the network round-trip is in flight. `onLoaded` flips it back.
          this.$parent.loadingOpportunities = true

          // MOCK BRANCH — client-side replica of what listOpportunities would
          // return
          if (USE_MOCKS) {
            return Promise.resolve({ data: this.$parent.buildMockResponse(data) })
          }

          // Client-side pagination. Fetch every opp for the
          // current (community, status) combo ONCE and cache it;
          const cacheKey = `${this.$parent.selectedCommunity || 'all'}|${this.$parent.selectedStatus || 'all'}`
          if (
            this.$parent.oppsCacheKey !== cacheKey ||
            !this.$parent.oppsCache
          ) {
            // NOTE: 500 is core-api's MAX_LIMIT. Above that we'd need real
            // server-side pagination — but a community with 500+ active opps
            // isn't a realistic MVP scenario. Server-side sort desc is
            // essential so newly created opps aren't dropped past the cap.
            const params = {
              limit: 500,
              offset: 0,
              sortBy: 'createdAt',
              sortOrder: 'desc',
            }
            if (this.$parent.selectedStatus && this.$parent.selectedStatus !== 'all') {
              params.status = this.$parent.selectedStatus
            }
            if (this.$parent.selectedCommunity && this.$parent.selectedCommunity !== 'all') {
              if (!this.$parent.communities.length) {
                await this.$parent.getCommunities()
              }
              const selectedComm = this.$parent.communities.find(
                (c) => c._id === this.$parent.selectedCommunity,
              )
              if (selectedComm && selectedComm.name) {
                params.community = selectedComm.name
              }
            }
            try {
              const authToken = localStorage.getItem('id_token')
              const res = await axios.get(`${CORE_API_URL}/api/opportunities`, {
                params,
                headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
              })
              const { opportunities } = coreApiPageToDashboardList(res.data)
              this.$parent.oppsCache = opportunities
              this.$parent.oppsCacheKey = cacheKey
            } catch (e) {
              if (e.response && (e.response.status === 401 || e.response.status === 403)) {
                localStorage.removeItem('authUser')
                this.$router.push('/login')
              }
              return { data: { opportunities: [], totalCount: 0 } }
            }
          }

          return {
            data: this.$parent.paginateFromCache(
              this.$parent.oppsCache,
              data,
            ),
          }
        },
        responseAdapter({ data }) {
          this.$parent.opportunities = data.opportunities
          return {
            data: data.opportunities,
            count: data.totalCount,
          }
        },
        filterable: true,
      },
      serverURL: `${CORE_API_URL}/api/opportunities`,
    }
  },
  async created() {
    this.token = localStorage.getItem('token')
    // Resolve initial community from context (deep-link or global switcher) so the first table load is scoped correctly
    const communityFromContext =
      this.$route.params.community_id ||
      this.$route.query.community_id ||
      localStorage.getItem('user_modal_option')
    if (communityFromContext && communityFromContext !== 'all') {
      this.selectedCommunity = communityFromContext
    }

    // Guard: block access when the selected community has the Recruiting
    // module disabled. Sidebar hides the link, but a direct URL should not
    // land on an empty page.
    if (communityFromContext && communityFromContext !== 'all') {
      const enabled = await this.isRecruitingEnabled(communityFromContext)
      if (!enabled) {
        this.$router.replace('/dashboard')
        return
      }
    }

    await this.getCommunities()
  },
  computed: {
    allCurrentPageSelected() {
      return (
        this.currentPageIds.length > 0 &&
        this.currentPageIds.every((id) => this.selectedIds.indexOf(id) !== -1)
      )
    },
    someCurrentPageSelected() {
      const hasSome = this.currentPageIds.some(
        (id) => this.selectedIds.indexOf(id) !== -1,
      )
      return hasSome && !this.allCurrentPageSelected
    },
    // Distinct statuses among the currently-selected rows (looked up from the
    // cached full list + current page).
    selectedStatuses() {
      const source = [...(this.oppsCache || []), ...(this.opportunities || [])]
      const byId = {}
      source.forEach((o) => {
        if (o && o._id) byId[o._id] = o.status
      })
      const set = new Set()
      this.selectedIds.forEach((id) => {
        if (byId[id]) set.add(byId[id])
      })
      return set
    },
    // Bulk bar options: hide a status only when every selected row already has
    // it (applying it would be a no-op for all of them).
    bulkActions() {
      return this.statusActions.filter(
        (a) =>
          !(this.selectedStatuses.size === 1 && this.selectedStatuses.has(a.value)),
      )
    },
  },
  methods: {
    // Check whether a community has the Recruiting module enabled in Hermes.
    // Called by the created() guard to redirect users who deep-linked here
    // for a community that never turned Recruiting on.
    async isRecruitingEnabled(communityId) {
      try {
        const { data } = await axios.get(
          `${CORE_API_URL}/api/recruiting/${communityId}`,
        )
        return data && data.enabled === true
      } catch (err) {
        return false
      }
    },
    /**
     * Bootstrap-vue badge variant per status.
     */
    statusVariant(status) {
      const variants = {
        active: 'success',
        in_review: 'warning',
        closed: 'secondary',
        rejected: 'danger',
        incomplete: 'info',
        quarantined: 'dark',
      }
      return variants[status] || 'secondary'
    },
    // Map internal status → admin-facing label. active/rejected read as
    // "Approved"/"Rejected" to match the Approve/Reject actions.
    statusLabel(status) {
      const labels = {
        in_review: 'In Review',
        active: 'Approved',
        closed: 'Closed',
        rejected: 'Rejected',
        incomplete: 'Incomplete',
        quarantined: 'Quarantined',
      }
      return labels[status] || status
    },
    // Adapt the core-api list response and apply client-side filters/sort.
    adaptListResponse(rawPage, params) {
      const { opportunities } = coreApiPageToDashboardList(rawPage)
      let filtered = opportunities
      // community filtering is done server-side via `?community=<code>`
      // in requestAdapter.
      if (this.selectedSource && this.selectedSource !== 'all') {
        filtered = filtered.filter(
          (o) => o.source && o.source.channel === this.selectedSource,
        )
      }
      if (this.selectedDates.startDate && this.selectedDates.endDate) {
        const start = new Date(this.selectedDates.startDate)
        const end = new Date(this.selectedDates.endDate)
        end.setHours(23, 59, 59, 999)
        filtered = filtered.filter((o) => {
          if (!o.submittedDate) return false
          const submitted = new Date(o.submittedDate)
          return submitted >= start && submitted <= end
        })
      }
      if (params && params.query) {
        const q = String(params.query).toLowerCase()
        filtered = filtered.filter((o) => {
          const haystack = [
            o.title,
            o.company,
            o.location,
            o.jobType,
            o.status,
            o.source && o.source.channel,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
          return haystack.includes(q)
        })
      }
      // Sort applied client-side because core-api's sort params are unverified.
      const orderBy = (params && params.orderBy) || 'submittedDate'
      const ascending = params && (params.ascending === 1 || params.ascending === true)
      filtered.sort((a, b) => {
        const aVal = a[orderBy]
        const bVal = b[orderBy]
        if (aVal == null && bVal == null) return 0
        if (aVal == null) return 1
        if (bVal == null) return -1
        if (aVal < bVal) return ascending ? -1 : 1
        if (aVal > bVal) return ascending ? 1 : -1
        return 0
      })

      return { opportunities: filtered, totalCount: filtered.length }
    },

    /**
     * Client-side replica of what a server-side listOpportunities would do:
     * apply the current filters, then sort, then paginate, and return the
     * page slice plus total count.
     */
    buildMockResponse(params) {
      let filtered = MOCK_OPPORTUNITIES.slice()

      // Every mock opportunity is anchored to DEMO_COMMUNITY_ID in
      // mocks/opportunities.js. Switching the global selector to a
      // different community returns zero rows here — simulating the real
      // backend's behaviour where an opp belongs to a single community.
      if (this.selectedCommunity && this.selectedCommunity !== 'all') {
        filtered = filtered.filter(
          o => o.communityId === this.selectedCommunity,
        )
      }

      if (this.selectedSource && this.selectedSource !== 'all') {
        // `source` is now an object ({ channel, firstName, lastName, email })
        // per PRD §5.3.2; filter on its channel field.
        filtered = filtered.filter(
          o => o.source && o.source.channel === this.selectedSource,
        )
      }
      if (this.selectedStatus && this.selectedStatus !== 'all') {
        filtered = filtered.filter(o => o.status === this.selectedStatus)
      }
      if (this.selectedDates.startDate && this.selectedDates.endDate) {
        const start = new Date(this.selectedDates.startDate)
        const end = new Date(this.selectedDates.endDate)
        // Inclusive on both ends; end of day on endDate so same-day matches.
        end.setHours(23, 59, 59, 999)
        filtered = filtered.filter(o => {
          const submitted = new Date(o.submittedDate)
          return submitted >= start && submitted <= end
        })
      }
      if (params.query) {
        const q = params.query.toLowerCase()
        // Match across every visible text field so the search bar covers
        // "everything you can see in the table" — title, employer, location,
        // type, status, source. Status / source filters above still narrow
        // independently when a user wants to combine them.
        filtered = filtered.filter(o => {
          const haystack = [
            o.title,
            o.company,
            o.location,
            o.jobType,
            o.status,
            o.source && o.source.channel,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
          return haystack.includes(q)
        })
      }

      // Sort
      const orderBy = params.orderBy || 'submittedDate'
      const ascending = params.ascending === 1 || params.ascending === true
      filtered.sort((a, b) => {
        const aVal = a[orderBy]
        const bVal = b[orderBy]
        if (aVal == null && bVal == null) return 0
        if (aVal == null) return 1
        if (bVal == null) return -1
        if (aVal < bVal) return ascending ? -1 : 1
        if (aVal > bVal) return ascending ? 1 : -1
        return 0
      })

      const totalCount = filtered.length

      // Paginate
      const page = params.page || 1
      const limit = params.limit || this.perPage || 25
      const startIdx = (page - 1) * limit
      const endIdx = startIdx + limit
      const pageItems = filtered.slice(startIdx, endIdx)

      return { opportunities: pageItems, totalCount }
    },
    onDateChange(res) {
      this.selectedDates.startDate = res.startDate
      this.selectedDates.endDate = res.endDate
      this.onFilterChange()
    },
    onFilterChange() {
      if (this.$refs.opportunitiesTable) {
        this.$refs.opportunitiesTable.setPage(1)
      }
      this.onLoaded()
    },
    onLoaded() {
      this.loadingOpportunities = false
      try {
        const rows =
          (this.$refs.opportunitiesTable &&
            this.$refs.opportunitiesTable.data) ||
          []
        this.currentPageIds = rows.map((r) => r._id)
      } catch (_) {
        this.currentPageIds = []
      }

      // Rebuild serverURL with the current filter set so the real branch
      // (when USE_MOCKS flips to false) passes them to the backend. The mock
      // branch ignores the URL and reads from `this.selected*` directly.
      const params = new URLSearchParams()
      if (this.selectedCommunity && this.selectedCommunity !== 'all') {
        params.set('community_id', this.selectedCommunity)
      }
      if (this.selectedSource && this.selectedSource !== 'all') {
        params.set('source', this.selectedSource)
      }
      if (this.selectedStatus && this.selectedStatus !== 'all') {
        params.set('status', this.selectedStatus)
      }
      params.set('startDate', this.selectedDates.startDate)
      params.set('endDate', this.selectedDates.endDate)
      this.serverURL = `${process.env.VUE_APP_API_BASE_URL}/dashboard/recruiting/opportunities?${params.toString()}`

      if (this.$helpers && this.$helpers.setTabindexOnTableCells) {
        this.$helpers.setTabindexOnTableCells()
      }
    },
    async getCommunities() {
      try {
        const { data } = await this.axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/dashboard/filter-community`,
          { headers: { 'x-access-token': this.token } },
        )
        this.communities = data
        if (this.$route.params.community_id) {
          const sc = data.filter(c => c._id == this.$route.params.community_id)
          if (sc.length <= 1) this.selectedCommunity = sc[0]._id
        } else {
          const valid = this.communities.some(c => c._id === this.selectedCommunity)
          if (!valid) {
            this.selectedCommunity =
              this.communities.length <= 1 ? this.communities[0]._id : 'all'
          }
        }
      } catch (err) {
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          localStorage.removeItem('authUser')
          this.$router.push('/login')
        }
      }
    },
    handleSearch(searchText) {
      if (this.$refs.opportunitiesTable) {
        this.$refs.opportunitiesTable.setFilter(searchText)
      }
    },
    handleLimitChange(limit) {
      this.perPage = limit
      if (this.$refs.opportunitiesTable) {
        this.$refs.opportunitiesTable.setLimit(limit)
      }
    },
    opportunityDetailHref(opportunityId) {
      // Detail opens in a new tab (SCRUM-719); resolve() keeps the router base.
      return this.$router.resolve(`/engagement/opportunities/${opportunityId}`).href
    },
    goToCreate() {
      // admin create form lives at a dedicated route (not a
      // modal) because the field set is too tall for a comfortable modal.
      this.$router.push('/engagement/opportunities/new')
    },
    // Row gear menu options: every status except the row's current one.
    rowStatusActions(status) {
      return this.statusActions.filter((a) => a.value !== status)
    },
    async applyStatusChange(row, newStatus) {
      if (!row || !newStatus || row.status === newStatus) return

      if (USE_MOCKS) {
        setMockOpportunityStatus(row._id, newStatus)
        this.refreshTable()
        return
      }

      try {
        const authToken = localStorage.getItem('id_token')
        await axios.patch(
          `${CORE_API_URL}/api/opportunities/${row._id}`,
          { status: newStatus },
          { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} },
        )
        this.refreshTable()
      } catch (err) {
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          localStorage.removeItem('authUser')
          this.$router.push('/login')
          return
        }
        alert(
          'We couldn\'t update this opportunity\'s status. Please try again in a moment.',
        )
      }
    },

    isSelected(id) {
      return this.selectedIds.indexOf(id) !== -1
    },
    toggleSelection(id) {
      const i = this.selectedIds.indexOf(id)
      if (i === -1) {
        this.selectedIds.push(id)
      } else {
        this.selectedIds.splice(i, 1)
      }
    },
    toggleSelectAllCurrentPage(checked) {
      if (checked) {
        this.currentPageIds.forEach((id) => {
          if (this.selectedIds.indexOf(id) === -1) {
            this.selectedIds.push(id)
          }
        })
      } else {
        this.selectedIds = this.selectedIds.filter(
          (id) => this.currentPageIds.indexOf(id) === -1,
        )
      }
    },
    clearSelection() {
      this.selectedIds = []
      this.bulkPendingStatus = null
    },
    // Toggle the target status for the bulk bar (click again to unset).
    selectBulkStatus(status) {
      this.bulkPendingStatus = this.bulkPendingStatus === status ? null : status
    },
    // Commit the chosen bulk status (the Apply button).
    async applyBulkPending() {
      if (!this.bulkPendingStatus) return
      await this.bulkSetStatus(this.bulkPendingStatus)
    },
    // Apply one status to every selected row. Backs the bulk Apply action.
    async bulkSetStatus(newStatus) {
      if (!newStatus || this.selectedIds.length === 0) return
      const ids = this.selectedIds.slice()

      if (USE_MOCKS) {
        ids.forEach((id) => setMockOpportunityStatus(id, newStatus))
        this.clearSelection()
        this.refreshTable()
        return
      }

      try {
        const authToken = localStorage.getItem('id_token')
        const authHeader = authToken ? { Authorization: `Bearer ${authToken}` } : {}
        for (const id of ids) {
          await axios.patch(
            `${CORE_API_URL}/api/opportunities/${id}`,
            { status: newStatus },
            { headers: authHeader },
          )
        }
        this.clearSelection()
        this.refreshTable()
      } catch (err) {
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          localStorage.removeItem('authUser')
          this.$router.push('/login')
          return
        }
        alert(
          'We couldn\'t apply the bulk status change. Some rows may have been updated. Please reload to verify.',
        )
      }
    },
    refreshTable() {
      this.oppsCache = null
      this.oppsCacheKey = null
      if (this.$refs.opportunitiesTable) {
        this.$refs.opportunitiesTable.refresh()
      }
    },
    // Finer client-side filters (source, date, search) shared by the table pager
    // and the CSV export; community + status are already applied server-side.
    applyClientFilters(list, query) {
      let filtered = (list || []).slice()

      if (this.selectedSource && this.selectedSource !== 'all') {
        filtered = filtered.filter(
          (o) => o.source && o.source.channel === this.selectedSource,
        )
      }
      if (this.selectedDates.startDate && this.selectedDates.endDate) {
        const start = new Date(this.selectedDates.startDate)
        const end = new Date(this.selectedDates.endDate)
        end.setHours(23, 59, 59, 999)
        filtered = filtered.filter((o) => {
          if (!o.submittedDate) return false
          const submitted = new Date(o.submittedDate)
          return submitted >= start && submitted <= end
        })
      }
      if (query) {
        const q = String(query).toLowerCase()
        filtered = filtered.filter((o) => {
          const haystack = [
            o.title,
            o.company,
            o.location,
            o.jobType,
            o.status,
            o.source && o.source.channel,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
          return haystack.includes(q)
        })
      }

      return filtered
    },

    // Download the current view as CSV: pull the full set from core-api /export,
    // re-apply the client-side filters, then hand flat rows to exportCSV.
    async exportOpportunities() {
      if (this.exporting) return
      this.exporting = true
      try {
        let rows
        if (USE_MOCKS) {
          rows = this.buildMockResponse({ limit: Number.MAX_SAFE_INTEGER, page: 1 }).opportunities
        } else {
          const params = { sortBy: 'createdAt', sortOrder: 'desc' }
          if (this.selectedStatus && this.selectedStatus !== 'all') {
            params.status = this.selectedStatus
          }
          if (this.selectedCommunity && this.selectedCommunity !== 'all') {
            if (!this.communities.length) await this.getCommunities()
            const selectedComm = this.communities.find(
              (c) => c._id === this.selectedCommunity,
            )
            if (selectedComm && selectedComm.name) params.community = selectedComm.name
          }
          const authToken = localStorage.getItem('id_token')
          const res = await axios.get(`${CORE_API_URL}/api/opportunities/export`, {
            params,
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
          })
          rows = (res.data || []).map(coreApiOpportunityToDashboard).filter(Boolean)
          rows = this.applyClientFilters(rows, this.searchQuery)
        }

        if (!rows.length) {
          alert('There are no opportunities matching the current filters to export.')
          return
        }
        await exportCSV(rows.map(opportunityToReportRow), 'opportunities', [], REPORT_DATE_FIELDS)
      } catch (err) {
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          localStorage.removeItem('authUser')
          this.$router.push('/login')
          return
        }
        alert("We couldn't export the opportunities. Please try again in a moment.")
      } finally {
        this.exporting = false
      }
    },

    // Client-side pager: takes the cached list of opportunities for the
    // current (community, status) combo, applies the finer filters
    // (source, dates, query) + sort, and returns the requested page slice.
    // Shape matches vue-tables-2's responseAdapter expectations.
    paginateFromCache(list, params) {
      let filtered = this.applyClientFilters(list, params && params.query)

      const orderBy = (params && params.orderBy) || 'submittedDate'
      const ascending = params && (params.ascending === 1 || params.ascending === true)
      filtered.sort((a, b) => {
        const aVal = a[orderBy]
        const bVal = b[orderBy]
        if (aVal == null && bVal == null) return 0
        if (aVal == null) return 1
        if (bVal == null) return -1
        if (aVal < bVal) return ascending ? -1 : 1
        if (aVal > bVal) return ascending ? 1 : -1
        return 0
      })

      const totalCount = filtered.length
      const page = (params && params.page) || 1
      const limit = (params && params.limit) || this.perPage || 10
      const startIdx = (page - 1) * limit
      const pageItems = filtered.slice(startIdx, startIdx + limit)

      return { opportunities: pageItems, totalCount }
    },
  },
  filters: {
    momentDate(date) {
      return date ? moment(date).format('MM/DD/YYYY') : ''
    },
  },
}
</script>

<style scoped>
/* Column widths for STATUS / SUBMITTED / OPENS / CLICKS / ACTION — keep them tight. */
::v-deep .col-status { width: 140px; min-width: 140px; }
::v-deep .col-submitted { width: 130px; min-width: 130px; }
::v-deep .col-opens { width: 90px; min-width: 90px; }
::v-deep .col-clicks { width: 90px; min-width: 90px; }
::v-deep .col-action { width: 90px; min-width: 90px; }

::v-deep .create-opp-btn {
  height: 35px;
  padding: 0 14px;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

::v-deep .card-body {
  padding-top: 0.5rem;
}

.bulk-action-slot {
  display: flex;
  align-items: center;
  height: 52px;
  margin: 0;
  padding: 0;
}
.bulk-action-bar {
  width: 95%;
  margin: 0 auto;
  height: 44px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  padding: 0.35rem 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}
.bulk-action-bar__count {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.75);
}
.bulk-action-bar__count strong {
  color: #fff;
  font-weight: 600;
}
/* Count sits left; the action buttons group is pushed to the right. */
.bulk-action-bar__actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.bulk-action-bar__actions .btn {
  font-size: 0.85rem;
}
.bulk-action-bar__clear {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;
  padding: 0 0.25rem;
}
.bulk-action-bar__clear:hover {
  color: #fff;
  text-decoration: underline;
}
/* Opportunity title acts as a link to the detail view. Keeps the table
   colour; underline + pointer signal it's clickable. */
.opp-title-link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}
.opp-title-link:hover,
.opp-title-link:focus {
  text-decoration: underline;
}
.bulk-fade-enter-active,
.bulk-fade-leave-active {
  transition: opacity 0.18s ease;
}
.bulk-fade-enter,
.bulk-fade-leave-to {
  opacity: 0;
}
::v-deep .selection-col {
  width: 44px;
  padding: 0 !important;
  text-align: center !important;
  vertical-align: middle !important;
}
::v-deep th.selection-col,
::v-deep td.selection-col {
  text-align: center !important;
  vertical-align: middle !important;
}
::v-deep .row-checkbox.custom-control {
  padding-left: 0 !important;
  margin: 0 !important;
  min-height: 1.25rem;
  height: 1.4rem;
  width: 1.4rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
}
::v-deep .row-checkbox .custom-control-label {
  cursor: pointer;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
}
::v-deep .row-checkbox .custom-control-input {
  cursor: pointer;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
}
::v-deep .row-checkbox .custom-control-label::before,
::v-deep .row-checkbox .custom-control-label::after {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 1.1rem;
  height: 1.1rem;
  transform: translate(-50%, -50%);
  cursor: pointer;
}
::v-deep .row-checkbox .custom-control-label::after {
  background-size: 70% 70%;
}
::v-deep .action-status-dropdown > .btn {
  padding: 0.25rem 0.5rem;
}
::v-deep .action-status-dropdown .dropdown-menu {
  z-index: 1050;
}

@media (max-width: 768px) {
  .bulk-action-slot {
    height: auto !important;
    min-height: 0 !important;
    display: block !important;
    margin-bottom: 0.75rem !important;
  }
  .bulk-action-bar {
    flex-direction: column !important;
    align-items: stretch !important;
    height: auto !important;
    gap: 0.5rem !important;
    padding: 0.6rem 0.75rem !important;
  }
  .bulk-action-bar__count {
    text-align: left;
  }
  .bulk-action-bar__actions {
    margin-left: 0 !important;
    width: 100%;
    flex-wrap: wrap;
  }
  .bulk-action-bar__clear {
    margin-left: auto;
  }

  ::v-deep .card-header {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  /* Header filter row */
  ::v-deep .card-header .header_table {
    float: none !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    width: 100%;
    margin: 0.5rem 0 0 0;
    padding-left: 0;
  }
  ::v-deep .card-header .header_table > li {
    display: block !important;
    width: 100% !important;
    margin: 0.25rem 0 !important;
    padding: 0 !important;
  }
  ::v-deep .card-header .header_table .custom-card-header-filter {
    width: 100% !important;
    max-width: none !important;
  }

  /* Datepicker internal layout. */
  ::v-deep .container-bak > .row-bak,
  ::v-deep .container-bak > .d-flex-bak {
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    padding-right: 0 !important;
    width: 100%;
  }
  ::v-deep .container-bak label {
    margin: 0 0 0.25rem 0 !important;
    width: 100%;
  }
  ::v-deep .container-bak .b-form-select,
  ::v-deep .container-bak select {
    width: 100% !important;
    max-width: none !important;
  }
  ::v-deep .controls {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  ::v-deep .controls > div {
    display: block !important;
    width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  ::v-deep .controls .mr-4 {
    margin-right: 0 !important;
    margin-bottom: 0.5rem !important;
  }
  ::v-deep .controls .ml-auto {
    margin-left: 0 !important;
  }
  ::v-deep .controls .form-group,
  ::v-deep .controls .b-form-group {
    width: 100% !important;
    margin-bottom: 0.5rem !important;
  }
  ::v-deep .controls .search-group,
  ::v-deep .controls .search-group .input-group,
  ::v-deep .controls .custom-limit-select,
  ::v-deep .limit-group {
    width: 100% !important;
  }
  ::v-deep .controls .input-group {
    flex-wrap: nowrap !important;
  }
  ::v-deep .controls .input-group .form-control {
    flex: 1 1 auto;
    min-width: 0;
    width: 1%;
  }
}
</style>
